import { useShiftNavigator } from "@dexteel/mesf-core";
import { useEffect, useState } from "react";
import { useTestShackReportFiltersQuery } from "./queries/useTestShackReportFiltersQuery";
import { useTestShackReportQuery } from "./queries/useTestShackReportQuery";
import {
	type TestShackReportUrlFilterUpdate,
	useTestShackReportUrlState,
} from "./useTestShackReportUrlState";

export const useTestShackReportData = (assetId: number) => {
	const [error, setError] = useState("");
	const { filters, updateFilters } = useTestShackReportUrlState();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const handleError = (queryError: Error) => setError(queryError.message);
	const handleFilterChange = (updates: TestShackReportUrlFilterUpdate) => {
		void updateFilters(updates);
	};
	const isShiftMode = filters.mode === "shift";
	const shiftId = isShiftMode
		? (shiftInfo?.CurrentShiftId ?? filters.shiftId)
		: null;
	const isRangeReady = !isShiftMode || shiftId !== null;
	const startDate = isShiftMode ? null : filters.startDate.toDate();
	const endDate = isShiftMode ? null : filters.endDate.toDate();

	const filterOptionsQuery = useTestShackReportFiltersQuery({
		assetId,
		startDate,
		endDate,
		shiftId,
		sectionId: filters.sectionId,
		eventId: filters.eventId,
		enabled: isRangeReady,
		onError: handleError,
	});
	const options = filterOptionsQuery.data;
	const hasStaleSection =
		filters.sectionId !== null &&
		options !== undefined &&
		!options.sections.some(
			(section) => section.SectionId === filters.sectionId,
		);
	const hasStaleEvent =
		filters.eventId !== null &&
		options !== undefined &&
		!options.events.some((event) => event.EventId === filters.eventId);
	const hasStaleHeat =
		filters.heatId !== null &&
		options !== undefined &&
		!options.heats.some((heat) => heat.HeatId === filters.heatId);

	const reportQuery = useTestShackReportQuery({
		assetId,
		startDate,
		endDate,
		shiftId,
		sectionId: filters.sectionId,
		eventId: filters.eventId,
		heatId: filters.heatId,
		problemsOnly: filters.problemsOnly,
		enabled:
			isRangeReady &&
			options !== undefined &&
			!hasStaleSection &&
			!hasStaleEvent &&
			!hasStaleHeat,
		onError: handleError,
	});

	useEffect(() => {
		if (isShiftMode) {
			getShiftDataFromAPI(null, filters.shiftId ?? 0, assetId);
		}
	}, [isShiftMode]);

	useEffect(() => {
		if (isShiftMode && shiftInfo?.CurrentShiftId) {
			void updateFilters({ shiftId: shiftInfo.CurrentShiftId });
		}
	}, [isShiftMode, shiftInfo]);

	useEffect(() => {
		if (hasStaleSection) {
			void updateFilters({ sectionId: null, eventId: null, heatId: null });
			return;
		}
		if (hasStaleEvent) {
			void updateFilters({ eventId: null, heatId: null });
			return;
		}
		if (hasStaleHeat) void updateFilters({ heatId: null });
	}, [hasStaleEvent, hasStaleHeat, hasStaleSection]);

	return {
		error,
		clearError: () => setError(""),
		handleError,
		filters,
		handleFilterChange,
		filterOptionsQuery,
		reportQuery,
		inspections: reportQuery.data?.inspections ?? [],
		isLoading: filterOptionsQuery.isLoading || reportQuery.isInitialLoading,
		isQueryError: filterOptionsQuery.isError || reportQuery.isError,
	};
};
